import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Sparkles, Heart } from "lucide-react";
import {
  GROOMING_STYLES,
  BODY_LENGTHS,
  FACE_STYLES,
  EAR_STYLES,
  TAIL_STYLES,
  getStyleById,
} from "@/lib/groomingStyles";
import { BREED_SUGGESTIONS, getBreedRecommendations } from "@/lib/breedRecommendations";

const Chip = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-all ${
      active
        ? "bg-[#0f172a] text-white border-[#0f172a]"
        : "bg-white text-[#0f172a] border-[#eae4d9] hover:bg-[#f3efe6]"
    }`}
  >
    {children}
  </button>
);

export default function StyleStep({ config, setConfig, usage, favoriteLook, onUseFavorite, onBack, onContinue }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Total remaining across free + paid + lifetime credit buckets.
  const freeRem = usage?.freeRemaining ?? Math.max(0, (usage?.freeAllowed ?? 2) - (usage?.freeUsed ?? 0));
  const paidRem = usage?.paidCredits ?? 0;
  const lifeRem = usage?.lifetimeCredits ?? 0;
  const remaining = freeRem + paidRem + lifeRem;
  const rec = useMemo(() => getBreedRecommendations(config.breedUnknown ? "" : config.breed), [config.breed, config.breedUnknown]);
  const recById = useMemo(() => Object.fromEntries(rec.picks.map((p) => [p.styleId, p])), [rec]);
  const orderedStyles = useMemo(() => {
    if (!rec.picks.length) return GROOMING_STYLES;
    const order = rec.picks.map((p) => p.styleId);
    const top = order.map((id) => GROOMING_STYLES.find((s) => s.id === id)).filter(Boolean);
    return [...top, ...GROOMING_STYLES.filter((s) => !order.includes(s.id))];
  }, [rec]);

  const selectStyle = (s) => setConfig({ ...config, style: s });
  const set = (k, v) => setConfig({ ...config, [k]: v });

  const canContinue =
    !!config.style &&
    (config.style.id !== "custom" || (config.customInstructions || "").trim().length > 3);

  return (
    <div className="max-w-3xl mx-auto px-4 pb-16" data-testid="style-step">
      <div className="text-center fade-up">
        <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
          Step 2 · Style
        </span>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
          What look are you going for?
        </h1>
        <p className="mt-3 text-sm sm:text-base text-[#475569]">
          Pick a starting style. You can fine-tune the details before generating.
        </p>
      </div>

      {favoriteLook && <FavoriteLookCard look={favoriteLook} config={config} onUse={onUseFavorite} />}

      {/* Optional dog info — drives breed-aware recommendations */}
      <div className="mt-8 rounded-2xl border border-[#eae4d9] bg-white p-5 shadow-[0_4px_20px_-2px_rgba(15,23,42,0.04)]" data-testid="about-dog-panel">
        <div className="text-sm font-semibold text-[#0f172a]">About your dog (optional)</div>
        <p className="text-xs text-[#64748b] mt-1">Tell us the breed and we'll recommend the styles that suit it best.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Dog's name" testId="dog-name-input" value={config.dogName || ""} onChange={(v) => set("dogName", v)} placeholder="e.g. Biscuit" />
          <Field label="Breed / type" testId="dog-breed-input" value={config.breed || ""} onChange={(v) => set("breed", v)} placeholder="e.g. Cavapoo" disabled={config.breedUnknown} list="breed-suggestions" />
          <Field label="Age" testId="dog-age-input" value={config.age || ""} onChange={(v) => set("age", v)} placeholder="e.g. 3 yrs" />
        </div>
        <datalist id="breed-suggestions">
          {BREED_SUGGESTIONS.map((b) => (
            <option key={b} value={b} />
          ))}
        </datalist>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-[#475569] cursor-pointer">
          <input
            type="checkbox"
            data-testid="breed-unknown-checkbox"
            checked={!!config.breedUnknown}
            onChange={(e) => setConfig({ ...config, breedUnknown: e.target.checked, breed: e.target.checked ? "" : config.breed })}
            className="rounded border-[#eae4d9] text-[#ff6b4a] focus:ring-[#ff6b4a]"
          />
          I don't know my dog's breed
        </label>
      </div>

      {rec.picks.length > 0 && (
        <div data-testid="breed-recommendation-banner" className="mt-6 flex items-start gap-3 rounded-2xl bg-[#ffe8e0]/60 border border-[#ffd8cf] px-4 py-3 fade-up">
          <Sparkles className="w-4 h-4 text-[#c84125] mt-0.5 shrink-0" />
          <p className="text-sm text-[#0f172a]">
            <span className="font-semibold">Recommended for {rec.known ? rec.group : (config.breed || "").trim()}.</span>{" "}
            <span className="text-[#475569]">
              {rec.known ? "We've moved the best-suited styles to the top." : "We don't know this breed well yet, so here are the most versatile cuts first."}
            </span>
          </p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {orderedStyles.map((s) => {
          const active = config.style?.id === s.id;
          const pick = recById[s.id];
          return (
            <button
              key={s.id}
              type="button"
              data-testid={`style-card-${s.id}`}
              data-recommended={pick ? "true" : undefined}
              onClick={() => selectStyle(s)}
              className={`text-left rounded-2xl overflow-hidden border-2 bg-white transition-all duration-200 group hover:-translate-y-0.5 ${
                active
                  ? "border-[#ff6b4a] ring-4 ring-[#ff6b4a]/15 shadow-[0_12px_32px_-8px_rgba(255,107,74,0.35)]"
                  : "border-transparent shadow-[0_4px_20px_-2px_rgba(15,23,42,0.06)] hover:shadow-[0_12px_32px_-4px_rgba(15,23,42,0.1)]"
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#f3efe6]">
                <img
                  src={s.image}
                  alt={s.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {pick && (
                  <span
                    data-testid={`recommended-badge-${s.id}`}
                    className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-[#0f172a]/85 backdrop-blur text-white text-[9px] font-bold tracking-widest uppercase px-2 py-1"
                  >
                    <Sparkles className="w-3 h-3 text-[#ff9d84]" /> Recommended
                  </span>
                )}
                {active && (
                  <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#ff6b4a] text-white flex items-center justify-center shadow">
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold text-[#0f172a]">{s.name}</div>
                <div className={`text-xs mt-0.5 line-clamp-3 ${pick ? "text-[#b45309]" : "text-[#64748b]"}`} data-testid={pick ? `recommended-reason-${s.id}` : undefined}>
                  {pick ? pick.reason : s.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {config.style?.id === "custom" && (
        <div
          data-testid="custom-style-panel"
          className="mt-6 rounded-2xl border border-[#eae4d9] bg-white p-5 fade-up"
        >
          <label className="text-sm font-semibold text-[#0f172a]">
            Describe the look you want
          </label>
          <textarea
            data-testid="custom-instructions-input"
            value={config.customInstructions || ""}
            onChange={(e) => set("customInstructions", e.target.value)}
            rows={3}
            placeholder="Example: Keep the ears long and fluffy, trim the body shorter, round the face, and keep the tail fluffy."
            className="mt-2 w-full rounded-xl border border-[#eae4d9] bg-[#fdfbf7] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#ff6b4a]/30 focus:border-[#ff6b4a] transition"
          />
        </div>
      )}

      {/* Advanced preferences */}
      <div className="mt-6 rounded-2xl border border-[#eae4d9] bg-white p-5">
        <button
          type="button"
          data-testid="toggle-advanced-btn"
          onClick={() => setShowAdvanced((s) => !s)}
          className="w-full flex items-center justify-between gap-2 text-left"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#0f172a]">
            <Sparkles className="w-4 h-4 text-[#ff6b4a]" /> Fine-tune style details (optional)
          </span>
          <span className="text-xs text-[#64748b]">{showAdvanced ? "Hide" : "Show"}</span>
        </button>
        {showAdvanced && (
          <div className="mt-4 space-y-4 fade-up">
            <PrefRow label="Body Length" testId="pref-body-length" options={BODY_LENGTHS} value={config.bodyLength} onChange={(v) => set("bodyLength", v)} />
            <PrefRow label="Face" testId="pref-face" options={FACE_STYLES} value={config.faceStyle} onChange={(v) => set("faceStyle", v)} />
            <PrefRow label="Ears" testId="pref-ears" options={EAR_STYLES} value={config.earStyle} onChange={(v) => set("earStyle", v)} />
            <PrefRow label="Tail" testId="pref-tail" options={TAIL_STYLES} value={config.tailStyle} onChange={(v) => set("tailStyle", v)} />
          </div>
        )}
      </div>

      {/* Optional dog info */}
      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button
          data-testid="style-back-btn"
          onClick={onBack}
          className="sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] font-medium px-5 py-3 border border-[#eae4d9] transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          data-testid="generate-preview-btn"
          disabled={!canContinue}
          onClick={onContinue}
          className={`flex-1 inline-flex items-center justify-center gap-2 rounded-full font-semibold px-6 py-3.5 transition-all duration-200 active:scale-[0.98] ${
            canContinue
              ? "bg-[#ff6b4a] hover:bg-[#e85535] text-white shadow-[0_8px_25px_-4px_rgba(255,107,74,0.35)] hover:shadow-[0_12px_30px_-4px_rgba(255,107,74,0.45)]"
              : "bg-[#e2ded4] text-[#94a3b8] cursor-not-allowed"
          }`}
        >
          Generate Preview <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <p data-testid="style-usage-hint" className="mt-3 text-center text-xs text-[#64748b]">
        {remaining > 0 ? (
          <>
            {freeRem > 0 && <>You have {freeRem} free preview{freeRem === 1 ? "" : "s"} remaining. </>}
            {paidRem > 0 && <>You have {paidRem} paid preview{paidRem === 1 ? "" : "s"} remaining. </>}
            {lifeRem > 0 && <>You have {lifeRem} lifetime preview{lifeRem === 1 ? "" : "s"} remaining.</>}
          </>
        ) : (
          <>All previews used. Pick a pack to keep going.</>
        )}
      </p>
    </div>
  );
}

const PrefRow = ({ label, options, value, onChange, testId }) => (
  <div>
    <div className="text-xs font-semibold text-[#0f172a] mb-2">{label}</div>
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {options.map((o) => (
        <Chip
          key={o}
          active={value === o}
          onClick={() => onChange(value === o ? "" : o)}
          testId={`${testId}-${o.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {o}
        </Chip>
      ))}
    </div>
  </div>
);

const Field = ({ label, value, onChange, placeholder, testId, disabled, list }) => (  <div>
    <label className="text-xs font-semibold text-[#0f172a]">{label}</label>
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      list={list}
      autoComplete="off"
      className="mt-1 w-full rounded-xl border border-[#eae4d9] bg-[#fdfbf7] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#ff6b4a]/30 focus:border-[#ff6b4a] transition disabled:opacity-50"
    />
  </div>
);


const FavoriteLookCard = ({ look, config, onUse }) => {
  const { dog, entry } = look;
  const style = getStyleById(entry.styleId) || { name: entry.styleName || "Custom" };
  const active = config.style?.id && config.style.id === (entry.styleId || style.id);
  const details = [
    entry.bodyLength && `Body ${entry.bodyLength}`,
    entry.faceStyle && `Face ${entry.faceStyle}`,
    entry.earStyle && `Ears ${entry.earStyle}`,
    entry.tailStyle && `Tail ${entry.tailStyle}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      data-testid="favorite-look-card"
      className="mt-8 flex items-center gap-3 sm:gap-4 rounded-2xl border border-[#ff6b4a] ring-1 ring-[#ff6b4a]/30 bg-[#fff6f3] p-3 sm:p-4 shadow-[0_8px_28px_-10px_rgba(255,107,74,0.35)] fade-up"
    >
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-[#f3efe6] shrink-0">
        {entry.previewImage ? (
          <img src={entry.previewImage} alt={style.name} className="w-full h-full object-cover" />
        ) : (
          <img src={style.image} alt={style.name} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-widest uppercase text-[#c84125]">
          <Heart className="w-3 h-3 fill-[#ff6b4a] text-[#ff6b4a]" />
          {dog.name ? `${dog.name}'s favourite` : "Your favourite look"}
        </div>
        <div className="mt-0.5 text-sm sm:text-base font-bold text-[#0f172a] truncate">{style.name}</div>
        {details && <div className="text-[11px] text-[#64748b] truncate">{details}</div>}
      </div>
      <button
        type="button"
        data-testid="use-favorite-look-btn"
        onClick={onUse}
        className={`shrink-0 inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-4 py-2.5 transition-all duration-200 active:scale-[0.98] ${
          active
            ? "bg-white text-[#0f172a] border border-[#eae4d9]"
            : "bg-[#ff6b4a] hover:bg-[#e85535] text-white shadow-[0_6px_18px_-4px_rgba(255,107,74,0.4)]"
        }`}
      >
        {active ? (
          <>
            <Check className="w-3.5 h-3.5" /> Selected
          </>
        ) : (
          <>
            <Sparkles className="w-3.5 h-3.5" /> Use this look
          </>
        )}
      </button>
    </div>
  );
};
